/**
 * Date/time formatting for chat.
 *
 * All server timestamps are UTC ISO strings; everything here renders them in the viewer's local
 * zone. Kept as plain functions rather than pipes so the message list can call them while
 * grouping (day separators) as well as while rendering.
 */

import { CHAT_MESSAGE_RETENTION_MS } from '../models/chat.models';

function toDate(iso: string | Date | null | undefined): Date | null {
  if (!iso) {
    return null;
  }
  const date = iso instanceof Date ? iso : new Date(iso);
  return isNaN(date.getTime()) ? null : date;
}

export function isSameDay(a: string | Date, b: string | Date): boolean {
  const first = toDate(a);
  const second = toDate(b);
  if (!first || !second) {
    return false;
  }
  return first.getFullYear() === second.getFullYear() && first.getMonth() === second.getMonth() && first.getDate() === second.getDate();
}

/** `10:10 AM` — the timestamp beside a message. */
export function timeLabel(iso: string | Date): string {
  const date = toDate(iso);
  if (!date) {
    return '';
  }
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** `Today` / `Yesterday` / `Fri, 23 May` — the centred separator between days. */
export function dayLabel(iso: string | Date): string {
  const date = toDate(iso);
  if (!date) {
    return '';
  }
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (isSameDay(date, today)) {
    return 'Today';
  }
  if (isSameDay(date, yesterday)) {
    return 'Yesterday';
  }

  const sameYear = date.getFullYear() === today.getFullYear();
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

/**
 * Compact age for a sidebar row: `2m`, `15m`, `1h`, `Yesterday`, `23 May`.
 * Slack-style — the list is scanned, not read.
 */
export function shortAgo(iso: string | Date | null | undefined): string {
  const date = toDate(iso);
  if (!date) {
    return '';
  }
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

  if (seconds < 60) {
    return 'now';
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24 && isSameDay(date, new Date())) {
    return `${hours}h`;
  }

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (isSameDay(date, yesterday)) {
    return 'Yesterday';
  }

  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: '2-digit' }) });
}

/** `Last seen 4:20 PM` / `Last seen 23 May` for an offline colleague. */
export function lastSeenLabel(iso: string | Date | null | undefined): string {
  const date = toDate(iso);
  if (!date) {
    return 'Offline';
  }
  if (isSameDay(date, new Date())) {
    return `Last seen ${timeLabel(date)}`;
  }
  return `Last seen ${dayLabel(date)}`;
}

/** `0:07` for a voice note. */
export function durationLabel(ms: number | null | undefined): string {
  const total = Math.max(0, Math.round((ms || 0) / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Is this message inside the retention window, i.e. not older than two weeks?
 *
 * The cutoff is computed per call rather than once at module load, so a tab left open for days
 * keeps ageing messages out instead of freezing the boundary at whenever the bundle was parsed.
 *
 * A timestamp that will not parse counts as **within** the window. Getting this backwards would
 * mean a malformed `created_at` silently deletes a live message from the user's screen, which is a
 * far worse failure than showing one message too many.
 */
export function isWithinRetention(iso: string | Date | null | undefined): boolean {
  const date = toDate(iso);
  if (!date) {
    return true;
  }
  return date.getTime() >= Date.now() - CHAT_MESSAGE_RETENTION_MS;
}

/**
 * Two consecutive messages from the same person within this window render as one visual block
 * (avatar and name printed once), the way Slack and Teams group them.
 */
export const CHAT_GROUPING_WINDOW_MS = 5 * 60 * 1000;

export function shouldGroupWithPrevious(
  current: { sender: { user_id: string }; created_at: string; system_event?: unknown },
  previous?: { sender: { user_id: string }; created_at: string; system_event?: unknown }
): boolean {
  if (!previous || current.system_event || previous.system_event) {
    return false;
  }
  if (current.sender?.user_id !== previous.sender?.user_id) {
    return false;
  }
  if (!isSameDay(current.created_at, previous.created_at)) {
    return false;
  }
  const gap = new Date(current.created_at).getTime() - new Date(previous.created_at).getTime();
  return gap >= 0 && gap < CHAT_GROUPING_WINDOW_MS;
}
